import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';

// Interfaces para los DTOs de Audit Log
export interface AuditLogResponseDTO {
  id: number;
  userId?: string;
  userEmail?: string;
  userFullName?: string;
  httpMethod: string;
  endpoint: string;
  requestHeaders?: string;
  requestBody?: string;
  responseBody?: string;
  statusCode: number;
  ipAddress?: string;
  userAgent?: string;
  timestamp: Date;
  durationMs: number;
  requestSizeBytes?: number;
  responseSizeBytes?: number;
  additionalContext?: string;
  isError: boolean;
  isDataModification: boolean;
  createdAt: Date;
  updatedAt: Date;
  createdBy?: string;
  updatedBy?: string;
  isDeleted: boolean;
}

export interface AuditLogRequestDTO {
  page?: number;
  pageSize?: number;
  httpMethod?: string;
  userId?: string;
  userEmail?: string;
  endpoint?: string;
  statusCode?: number;
  statusCodeMin?: number;
  statusCodeMax?: number;
  dateFrom?: Date;
  dateTo?: Date;
  onlyErrors?: boolean;
  onlyDataModifications?: boolean;
  ipAddress?: string;
  minDuration?: number;
  maxDuration?: number;
  orderBy?: string;
  orderDirection?: string;
}

export interface AuditLogSummaryDTO {
  totalRecords: number;
  totalErrors: number;
  errorPercentage: number;
  totalDataModifications: number;
  uniqueUsers: number;
  averageDuration: number;
  maxDuration: number;
  httpMethodStats: HttpMethodStatsDTO[];
  topEndpoints: EndpointStatsDTO[];
  topUsers: UserStatsDTO[];
  statusCodeStats: StatusCodeStatsDTO[];
}

export interface HttpMethodStatsDTO {
  method: string;
  count: number;
  percentage: number;
}

export interface EndpointStatsDTO {
  endpoint: string;
  count: number;
  averageDuration: number;
}

export interface UserStatsDTO {
  userId?: string;
  userEmail?: string;
  userFullName?: string;
  requestCount: number;
  errorCount: number;
  lastActivity: Date;
}

export interface StatusCodeStatsDTO {
  statusCode: number;
  count: number;
  percentage: number;
  description: string;
}

export interface PaginatedAuditLogResponse {
  data: AuditLogResponseDTO[];
  totalRecords: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface CleanupResponse {
  message: string;
  deletedCount: number;
  olderThanDays: number;
  cutoffDate: Date;
}

export interface SystemInfo {
  version: string;
  features: string[];
  limits: {
    maxPageSize: number;
    defaultPageSize: number;
    minCleanupDays: number;
    maxCleanupDays: number;
  };
}

@Injectable({
  providedIn: 'root'
})
export class AuditLogService {

  private readonly apiUrl = `${environment.apiUrl}/AuditLog`;

  constructor(private http: HttpClient) { }

  /**
   * Obtiene logs de auditoría con filtros avanzados (POST)
   */
  getAuditLogs(request: AuditLogRequestDTO): Observable<PaginatedAuditLogResponse> {
    return this.http.post<PaginatedAuditLogResponse>(`${this.apiUrl}/search`, request);
  }

  /**
   * Obtiene logs de auditoría con parámetros GET (para casos simples)
   */
  getAuditLogsSimple(params: {
    page?: number;
    pageSize?: number;
    httpMethod?: string;
    userEmail?: string;
    endpoint?: string;
    statusCode?: number;
    dateFrom?: Date;
    dateTo?: Date;
    onlyErrors?: boolean;
    orderBy?: string;
    orderDirection?: string;
  } = {}): Observable<PaginatedAuditLogResponse> {
    let httpParams = new HttpParams();

    // Agregar parámetros no nulos
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined) {
        if (value instanceof Date) {
          httpParams = httpParams.set(key, value.toISOString());
        } else {
          httpParams = httpParams.set(key, value.toString());
        }
      }
    });

    return this.http.get<PaginatedAuditLogResponse>(this.apiUrl, { params: httpParams });
  }

  /**
   * Obtiene un log de auditoría específico por ID
   */
  getAuditLogById(id: number): Observable<AuditLogResponseDTO> {
    return this.http.get<AuditLogResponseDTO>(`${this.apiUrl}/${id}`);
  }

  /**
   * Obtiene estadísticas resumidas de auditoría
   */
  getAuditSummary(dateFrom?: Date, dateTo?: Date): Observable<AuditLogSummaryDTO> {
    let params = new HttpParams();
    
    if (dateFrom) {
      params = params.set('dateFrom', dateFrom.toISOString());
    }
    
    if (dateTo) {
      params = params.set('dateTo', dateTo.toISOString());
    }

    return this.http.get<AuditLogSummaryDTO>(`${this.apiUrl}/summary`, { params });
  }

  /**
   * Limpia logs antiguos (solo administradores)
   */
  cleanupOldLogs(olderThanDays: number = 90): Observable<CleanupResponse> {
    const params = new HttpParams().set('olderThanDays', olderThanDays.toString());
    return this.http.delete<CleanupResponse>(`${this.apiUrl}/cleanup`, { params });
  }

  /**
   * Obtiene métodos HTTP únicos disponibles
   */
  getHttpMethods(): Observable<string[]> {
    return this.http.get<string[]>(`${this.apiUrl}/http-methods`);
  }

  /**
   * Obtiene información del sistema de auditoría
   */
  getSystemInfo(): Observable<SystemInfo> {
    return this.http.get<SystemInfo>(`${this.apiUrl}/system-info`);
  }

  /**
   * Método de utilidad para formatear el tamaño en bytes
   */
  formatBytes(bytes: number | undefined): string {
    if (!bytes || bytes === 0) return '0 Bytes';
    
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Método de utilidad para formatear duración
   */
  formatDuration(ms: number): string {
    if (ms < 1000) return `${ms}ms`;
    if (ms < 60000) return `${(ms / 1000).toFixed(2)}s`;
    if (ms < 3600000) return `${(ms / 60000).toFixed(2)}m`;
    return `${(ms / 3600000).toFixed(2)}h`;
  }

  /**
   * Método de utilidad para obtener la clase CSS según el código de estado
   */
  getStatusCodeClass(statusCode: number): string {
    if (statusCode >= 200 && statusCode < 300) return 'text-green-600 bg-green-100';
    if (statusCode >= 300 && statusCode < 400) return 'text-yellow-600 bg-yellow-100';
    if (statusCode >= 400 && statusCode < 500) return 'text-red-600 bg-red-100';
    if (statusCode >= 500) return 'text-red-800 bg-red-200';
    return 'text-gray-600 bg-gray-100';
  }

  /**
   * Método de utilidad para obtener la clase CSS según el método HTTP
   */
  getHttpMethodClass(method: string): string {
    switch (method?.toUpperCase()) {
      case 'GET': return 'text-blue-600 bg-blue-100';
      case 'POST': return 'text-green-600 bg-green-100';
      case 'PUT': return 'text-yellow-600 bg-yellow-100';
      case 'PATCH': return 'text-orange-600 bg-orange-100';
      case 'DELETE': return 'text-red-600 bg-red-100';
      case 'OPTIONS': return 'text-purple-600 bg-purple-100';
      case 'HEAD': return 'text-gray-600 bg-gray-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  }

  /**
   * Método de utilidad para truncar texto
   */
  truncateText(text: string | undefined, maxLength: number = 100): string {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  }

  /**
   * Método de utilidad para formatear fecha/hora
   */
  formatDateTime(date: Date | string): string {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  }
}
